import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { getAllEvents } from "../../../services/eventServices";

import { useUser } from "../../Home/UserContext";

import HeroImage from "../../../components/Hero/HeroImage";
import ScrollIndicator from "../../Home/ScrollIndicator";

import useScrollToTop from "../../../hooks/useScollToTop";

import ActiveFilterChips from "./components/ActiveFilterChips";
import CalendarFiltersDrawer from "./components/CalendarFiltersDrawer";
import CalendarLegend from "./components/CalendarLegend";
import CalendarToolbar from "./components/CalendarToolbar";
import DayEventsDrawer from "./components/DayEventsDrawer";
import EventsCardsView from "./components/EventsCardsView";
import MonthCalendar from "./components/MonthCalendar";
import MonthNavigation from "./components/MonthNavigation";

import EventDetailsModal from "./EventDetailsModal";

import useCalendarFilters from "./hooks/useCalendarFilters";

import CalendarSkeleton from "./ui/CalendarSkeleton";

import {
  getEventDateRange,
  getEventsForMonth,
  shiftMonth,
  sortEventsByStartDate,
  startOfMonth,
  toDateKey,
} from "./utils/calendarUtils";

const DESKTOP_VIEW_STORAGE_KEY =
  "ligue-calendar-desktop-view";

const MOBILE_VIEW_STORAGE_KEY =
  "ligue-calendar-mobile-view";

function getInitialViewMode() {
  if (typeof window === "undefined") {
    return "calendar";
  }

  try {
    const isMobile = window.matchMedia(
      "(max-width: 767px)"
    ).matches;

    const storageKey = isMobile
      ? MOBILE_VIEW_STORAGE_KEY
      : DESKTOP_VIEW_STORAGE_KEY;

    const savedView =
      window.localStorage.getItem(storageKey);

    if (
      savedView === "calendar" ||
      savedView === "cards"
    ) {
      return savedView;
    }

    /*
     * Sur smartphone, la vue cartes est plus facile
     * à consulter lors de la première visite.
     */
    return isMobile ? "cards" : "calendar";
  } catch {
    return "calendar";
  }
}

export default function CalendrierLigue() {
  useScrollToTop();

  const { user } = useUser();

  const canExport = [1, 3, 8].includes(
    user?.role_id
  );

  const [rawEvents, setRawEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [viewMode, setViewMode] = useState(
    getInitialViewMode
  );

  const [currentMonth, setCurrentMonth] =
    useState(() => startOfMonth(new Date()));

  const [filtersOpen, setFiltersOpen] =
    useState(false);

  const [selectedEvent, setSelectedEvent] =
    useState(null);

  const [selectedDay, setSelectedDay] =
    useState(null);

  const [showPast, setShowPast] =
    useState(false);

  const [sortOrder, setSortOrder] =
    useState("asc");

  const calendarFilters =
    useCalendarFilters(rawEvents);

  const {
    legendEvents,
    filteredEvents,

    hiddenPaletteKeys,
    togglePaletteVisibility,

    activeChips,
    activeFilterCount,

    removeFilterChip,
    clearFilters,
  } = calendarFilters;

  const fetchEvents = useCallback(async () => {
    setLoading(true);

    try {
      const data = await getAllEvents();

      setRawEvents(
        Array.isArray(data) ? data : []
      );
    } catch (error) {
      console.error(
        "Erreur lors du chargement du calendrier :",
        error
      );

      setRawEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    try {
      const isMobile = window.matchMedia(
        "(max-width: 767px)"
      ).matches;

      const storageKey = isMobile
        ? MOBILE_VIEW_STORAGE_KEY
        : DESKTOP_VIEW_STORAGE_KEY;

      window.localStorage.setItem(
        storageKey,
        viewMode
      );
    } catch {
      // localStorage indisponible.
    }
  }, [viewMode]);

  const monthEvents = useMemo(() => {
    return getEventsForMonth(
      filteredEvents,
      currentMonth
    );
  }, [filteredEvents, currentMonth]);

  const monthLegendEvents = useMemo(() => {
    return getEventsForMonth(
      legendEvents,
      currentMonth
    );
  }, [legendEvents, currentMonth]);

  const cardEvents = useMemo(() => {
    const todayKey = toDateKey(new Date());

    const events = filteredEvents.filter(
      (event) => {
        const { startKey, endKey } =
          getEventDateRange(event);

        if (
          !startKey ||
          !endKey ||
          !todayKey
        ) {
          return false;
        }

        return showPast
          ? endKey < todayKey
          : endKey >= todayKey;
      }
    );

    events.sort(sortEventsByStartDate);

    if (sortOrder === "desc") {
      events.reverse();
    }

    return events;
  }, [
    filteredEvents,
    showPast,
    sortOrder,
  ]);

  const eventsToExport =
    viewMode === "calendar"
      ? monthEvents
      : cardEvents;

  const handleReset = () => {
    clearFilters();
    setShowPast(false);
    setSortOrder("asc");
  };

  const handleOpenDay = (date, events) => {
    setSelectedDay({
      date,
      events,
    });
  };

  const handleSelectEventFromDay = (event) => {
    setSelectedDay(null);
    setSelectedEvent(event);
  };

  return (
    <div className="w-full bg-slate-50 pb-16 pt-[70px] dark:bg-slate-950">
      <ScrollIndicator />

      <HeroImage
        title="Calendrier Ligue Nouvelle-Aquitaine & Comités"
        subtitle="Compétitions, stages, formations et animations"
        imageUrl="/img/cover/cover_calendar.jpeg"
      />

      <main className="mx-auto w-full max-w-[1600px] space-y-4 px-3 py-5 sm:px-4 lg:px-6">
        <CalendarToolbar
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          filtersOpen={filtersOpen}
          activeFilterCount={
            activeFilterCount
          }
          exportEvents={eventsToExport}
          canExport={canExport}
          loading={loading}
          onOpenFilters={() =>
            setFiltersOpen(true)
          }
          onRefresh={fetchEvents}
        />

        <ActiveFilterChips
          chips={activeChips}
          onRemove={removeFilterChip}
          onClear={handleReset}
        />

        {viewMode === "calendar" && (
          <section className="space-y-2">
            <MonthNavigation
              monthDate={currentMonth}
              monthEvents={monthEvents}
              monthSourceEvents={filteredEvents}
              legendEvents={monthLegendEvents}
              hiddenPaletteKeys={
                hiddenPaletteKeys
              }
              onPreviousMonth={() =>
                setCurrentMonth((month) =>
                  shiftMonth(month, -1)
                )
              }
              onNextMonth={() =>
                setCurrentMonth((month) =>
                  shiftMonth(month, 1)
                )
              }
              onToday={() =>
                setCurrentMonth(
                  startOfMonth(new Date())
                )
              }
              onMonthChange={setCurrentMonth}
              onTogglePalette={
                togglePaletteVisibility
              }
            />

            {!loading &&
              monthEvents.length === 0 && (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-5 py-6 text-center dark:border-white/15 dark:bg-slate-900">
                  <p className="font-black text-slate-700 dark:text-slate-200">
                    Aucun événement pour ce mois.
                  </p>

                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Changez de mois ou modifiez les
                    filtres actifs.
                  </p>
                </div>
              )}

            {loading &&
            rawEvents.length === 0 ? (
              <CalendarSkeleton />
            ) : (
              <MonthCalendar
                monthDate={currentMonth}
                events={filteredEvents}
                onSelectEvent={
                  setSelectedEvent
                }
                onOpenDay={handleOpenDay}
              />
            )}
          </section>
        )}

        {viewMode === "cards" && (
          <section className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm dark:border-white/10 dark:bg-slate-900">
              <CalendarLegend
                events={legendEvents}
                hiddenPaletteKeys={
                  hiddenPaletteKeys
                }
                onTogglePalette={
                  togglePaletteVisibility
                }
              />
            </div>

            <EventsCardsView
              events={cardEvents}
              loading={loading}
              showPast={showPast}
              sortOrder={sortOrder}
              onChangeShowPast={setShowPast}
              onChangeSortOrder={setSortOrder}
              onSelectEvent={
                setSelectedEvent
              }
            />
          </section>
        )}
      </main>

      <CalendarFiltersDrawer
        isOpen={filtersOpen}
        onClose={() =>
          setFiltersOpen(false)
        }
        filteredEventsCount={
          filteredEvents.length
        }
        filters={calendarFilters}
      />

      <DayEventsDrawer
        dayData={selectedDay}
        onClose={() => setSelectedDay(null)}
        onSelectEvent={
          handleSelectEventFromDay
        }
      />

      <EventDetailsModal
        isOpen={Boolean(selectedEvent)}
        onClose={() =>
          setSelectedEvent(null)
        }
        event={selectedEvent}
      />
    </div>
  );
}